/**
 * AgentPanel Component
 * Main container for AI agent interaction in Office Add-ins
 */

import React, { useState, useCallback } from 'react'
import { AgentPanelHeader } from './AgentPanelHeader'
import { AgentStatusBar } from './AgentStatusBar'
import { AgentConversationArea } from './AgentConversationArea'
import { AgentInputArea } from './AgentInputArea'
import { useAgentPanel } from '../../hooks/useAgentPanel'
import type { AgentPanelConfig, AgentAction, AgentType, AgentQuickAction } from '../../types'

interface AgentPanelProps {
  /**
   * Configuration for the agent panel based on Office app type
   */
  config: AgentPanelConfig
  /**
   * Callback when an action is triggered from the panel
   */
  onAction?: (action: AgentAction) => void
}

/**
 * Main Agent Panel component for Office Add-ins
 * Provides conversational AI assistance within Office applications
 */
export const AgentPanel: React.FC<AgentPanelProps> = ({ config, onAction }) => {
  const {
    agents,
    currentAgent,
    panelState,
    isLoading,
    error,
    conversation,
    isStreaming,
    selectAgent,
    sendMessage,
    executeQuickAction,
    clearConversation,
    // setPanelState - available for future use (compliance alerts, document complete states)
  } = useAgentPanel()

  const [isExpanded, setIsExpanded] = useState(false)

  const handleAction = useCallback(
    (action: AgentAction) => {
      onAction?.(action)
    },
    [onAction]
  )

  const handleAgentSelect = useCallback(
    async (agentType: AgentType) => {
      const agent = agents.find((a) => a.agentType === agentType)
      if (agent) {
        await selectAgent(agent.id)
      }
    },
    [agents, selectAgent]
  )

  const handleSendMessage = useCallback(
    async (message: string) => {
      await sendMessage(message)
    },
    [sendMessage]
  )

  const handleQuickAction = useCallback(
    async (action: AgentQuickAction) => {
      await executeQuickAction(action)
    },
    [executeQuickAction]
  )

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const handleClearError = useCallback(() => {
    // Clear error by clearing conversation (which resets error state)
    clearConversation()
  }, [clearConversation])

  const messages = conversation?.messages || []

  const renderContent = () => {
    switch (panelState) {
      case 'welcome':
        return (
          <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
            <div className="mb-4 text-4xl">
              {currentAgent ? '👋' : '🤖'}
            </div>
            <h2 className="mb-2 text-lg font-semibold text-gray-800">
              {currentAgent
                ? `Hi! I'm ${currentAgent.name}`
                : 'Welcome to Templatean AI'}
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              {currentAgent?.description ||
                'Select an agent above to get started with AI-powered document assistance.'}
            </p>
            {currentAgent && (
              <p className="text-xs text-gray-500">
                How can I help you today?
              </p>
            )}
          </div>
        )

      case 'conversation':
        return (
          <AgentConversationArea
            messages={messages}
            isLoading={isLoading || isStreaming}
            currentAgent={currentAgent}
            onAction={handleAction}
          />
        )

      case 'compliance-alert':
        return (
          <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
            <div className="mb-4 text-4xl">⚠️</div>
            <h2 className="mb-2 text-lg font-semibold text-amber-700">
              Compliance Alert
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              Some content in your document may not comply with brand guidelines.
              Review the suggestions below.
            </p>
          </div>
        )

      case 'document-complete':
        return (
          <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
            <div className="mb-4 text-4xl">✅</div>
            <h2 className="mb-2 text-lg font-semibold text-green-700">
              Document Complete
            </h2>
            <p className="text-sm text-gray-600">
              Great job! Your document is ready and compliant with all guidelines.
            </p>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={`flex h-full w-[350px] flex-col bg-white ${
        isExpanded ? 'fixed inset-0 z-50 w-full' : ''
      }`}
    >
      <AgentPanelHeader
        agentName={currentAgent?.name}
        agentStatus={isLoading || isStreaming ? 'thinking' : 'ready'}
        isExpanded={isExpanded}
        onToggleExpanded={handleToggleExpanded}
      />

      <AgentStatusBar
        agents={agents}
        currentAgentType={currentAgent?.agentType}
        onSelectAgent={handleAgentSelect}
      />

      {error && (
        <div className="flex items-center justify-between bg-red-50 px-4 py-2">
          <span className="text-sm text-red-700">{error}</span>
          <button
            type="button"
            onClick={handleClearError}
            className="text-red-700 hover:text-red-900"
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        {renderContent()}
      </div>

      <AgentInputArea
        quickActions={config.quickActions}
        onSendMessage={handleSendMessage}
        onQuickAction={handleQuickAction}
        disabled={!currentAgent || isLoading}
        isLoading={isLoading || isStreaming}
      />
    </div>
  )
}

export default AgentPanel
